/**
 * codelistResolver — INIT-2026-Q3-SCENE-HIERARCHY V3.
 *
 * Fetches and parses CityGML codelist XML files (gml:Dictionary format).
 * Maps raw codes (e.g. "441") to human-readable labels.
 * Results are cached per URL. Purely display-time resolution — raw codes
 * remain in the data; resolution happens only at render.
 *
 * CityGML codelist XML structure:
 * ```xml
 * <gml:Dictionary ...>
 *   <gml:dictionaryEntry>
 *     <gml:Definition gml:id="id_441">
 *       <gml:description>商業施設</gml:description>
 *       <gml:name>441</gml:name>
 *     </gml:Definition>
 *   </gml:dictionaryEntry>
 *   ...
 * </gml:Dictionary>
 * ```
 */

export interface CodelistEntry {
  code: string;
  label: string;
}

export type CodelistMap = ReadonlyMap<string, string>;

const cache = new Map<string, CodelistMap>();
const inflight = new Map<string, Promise<CodelistMap | null>>();

/**
 * Parse a CityGML codelist XML string into a code→label Map.
 */
export function parseCodelistXml(xml: string): CodelistMap {
  const map = new Map<string, string>();
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "text/xml");

  const definitions = doc.querySelectorAll("Definition");
  for (const def of definitions) {
    const nameEl =
      def.querySelector("name") ??
      def.getElementsByTagNameNS("http://www.opengis.net/gml", "name")[0];
    const descEl =
      def.querySelector("description") ??
      def.getElementsByTagNameNS("http://www.opengis.net/gml", "description")[0];
    if (nameEl?.textContent && descEl?.textContent) {
      map.set(nameEl.textContent.trim(), descEl.textContent.trim());
    }
  }
  return map;
}

/**
 * Fetch and cache a codelist XML. Returns null on any failure.
 */
export async function fetchCodelist(
  url: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CodelistMap | null> {
  if (cache.has(url)) return cache.get(url)!;
  if (inflight.has(url)) return inflight.get(url)!;

  const promise = (async () => {
    try {
      const resp = await fetchImpl(url);
      if (!resp.ok) return null;
      const text = await resp.text();
      const map = parseCodelistXml(text);
      cache.set(url, map);
      return map;
    } catch {
      return null;
    } finally {
      inflight.delete(url);
    }
  })();

  inflight.set(url, promise);
  return promise;
}

/**
 * Resolve a raw code to "label (code)" format.
 * Returns the raw code unchanged if no codelist is available or the code is unknown.
 */
export function resolveCodeToLabel(
  code: string | number | null | undefined,
  codelist: CodelistMap | null | undefined,
): string {
  if (code === null || code === undefined) return "";
  const raw = String(code);
  if (!codelist) return raw;
  const label = codelist.get(raw);
  return label ? `${label} (${raw})` : raw;
}

/**
 * Known CityGML attribute keys → codelist URL patterns.
 * These are the standard PLATEAU codelist file names.
 */
export const CODELIST_ATTRIBUTES: ReadonlyMap<string, string> = new Map([
  ["bldg:usage", "Building_usage.xml"],
  ["usage", "Building_usage.xml"],
  ["bldg:class", "Building_class.xml"],
  ["class", "Building_class.xml"],
  ["roofType", "Building_roofType.xml"],
  ["bldg:roofType", "Building_roofType.xml"],
  ["landUseType", "LandUse_class.xml"],
  ["uro:BuildingDetailAttribute_uro:landUseType", "LandUse_class.xml"],
  ["fireproofStructureType", "Building_fireproofStructureType.xml"],
  ["uro:BuildingDetailAttribute_uro:fireproofStructureType", "Building_fireproofStructureType.xml"],
  ["uro:BuildingDetailAttribute_uro:districtsAndZonesType", "DistrictsAndZones_class.xml"],
  ["urbanPlanType", "DistrictsAndZones_class.xml"],
]);

/**
 * Check if a given attribute key is known to have a codelist.
 */
export function isCodelistAttribute(key: string): boolean {
  return CODELIST_ATTRIBUTES.has(key);
}

/** Clear the cache (for testing). */
export function clearCodelistCache(): void {
  cache.clear();
  inflight.clear();
}
